import { supabase } from '@/integrations/supabase/client';
import { InvoiceData, LineItem } from '@/features/onboarding/phase2/InvoiceFieldEditor';

export interface ParsingTemplate {
  id?: string;
  company_id: string;
  vendor_id: string;
  file_type: 'pdf' | 'csv' | 'excel' | 'xlsx' | 'xls' | 'image' | 'jpg' | 'jpeg' | 'png' | 'heic';
  field_mappings: {
    total_amount?: { location: string; pattern?: string };
    subtotal?: { location: string; pattern?: string };
    tax?: { location: string; pattern?: string };
    payment_method?: { location: string; pattern?: string };
    invoice_date?: { location: string; pattern?: string };
    invoice_number?: { location: string; pattern?: string };
    line_items?: {
      table_location?: string;
      columns?: {
        description?: { index: number; pattern?: string };
        quantity?: { index: number; pattern?: string };
        unit_price?: { index: number; pattern?: string };
        total?: { index: number; pattern?: string };
      };
    };
  };
  confidence_score: number;
  training_samples: number;
  training_invoice_ids?: string[];
  is_active?: boolean;
}

export interface TrainingSample {
  invoice_id: string;
  corrected_data: InvoiceData;
  original_ocr_data: any;
}

/**
 * Save a training sample (user-corrected invoice data)
 */
export async function saveTrainingSample(
  companyId: string,
  vendorId: string,
  invoiceId: string,
  correctedData: InvoiceData,
  originalOcrData: any
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('vendor_invoice_training_samples')
    .upsert(
      {
        company_id: companyId,
        vendor_id: vendorId,
        invoice_id: invoiceId,
        corrected_line_items: correctedData.line_items,
        corrected_total_amount: correctedData.total_amount,
        corrected_subtotal: correctedData.subtotal,
        corrected_tax_amount: correctedData.tax_amount,
        corrected_payment_method: correctedData.payment_method,
        corrected_balance: correctedData.balance,
        corrected_invoice_date: correctedData.invoice_date,
        corrected_invoice_number: correctedData.invoice_number,
        original_ocr_data: originalOcrData,
        corrected_by: user.id,
      },
      {
        onConflict: 'invoice_id',
      }
    );

  if (error) throw error;
}

/**
 * Get training samples for a vendor
 */
export async function getTrainingSamples(
  companyId: string,
  vendorId: string
): Promise<TrainingSample[]> {
  const { data, error } = await supabase
    .from('vendor_invoice_training_samples')
    .select('invoice_id, corrected_line_items, corrected_total_amount, corrected_subtotal, corrected_tax_amount, corrected_payment_method, corrected_balance, corrected_invoice_date, corrected_invoice_number, original_ocr_data')
    .eq('company_id', companyId)
    .eq('vendor_id', vendorId)
    .order('created_at', { ascending: true });

  if (error) throw error;

  return (data || []).map((sample: any) => ({
    invoice_id: sample.invoice_id,
    corrected_data: {
      line_items: sample.corrected_line_items || [],
      total_amount: sample.corrected_total_amount,
      subtotal: sample.corrected_subtotal,
      tax_amount: sample.corrected_tax_amount,
      payment_method: sample.corrected_payment_method,
      balance: sample.corrected_balance,
      invoice_date: sample.corrected_invoice_date,
      invoice_number: sample.corrected_invoice_number,
    },
    original_ocr_data: sample.original_ocr_data,
  }));
}

/**
 * Generate parsing template from training samples
 * This analyzes patterns in corrected data to create extraction rules
 */
export async function generateParsingTemplate(
  companyId: string,
  vendorId: string,
  fileType: string,
  samples: TrainingSample[]
): Promise<ParsingTemplate> {
  if (samples.length < 2) {
    throw new Error('Need at least 2 training samples to generate a template');
  }

  // Analyze patterns across samples
  const fieldMappings: ParsingTemplate['field_mappings'] = {};

  // For CSV/Excel files, analyze column positions
  if (fileType === 'csv' || fileType === 'excel' || fileType === 'xlsx' || fileType === 'xls') {
    // Try to detect column patterns from line items
    const allLineItems = samples.flatMap((s) => s.corrected_data.line_items);
    if (allLineItems.length > 0) {
      // This is a simplified version - in production, you'd analyze the original OCR data
      // to determine which columns contain which fields
      fieldMappings.line_items = {
        columns: {
          description: { index: 0 }, // Assume first column is description
          quantity: { index: 1 }, // Assume second is quantity
          unit_price: { index: 2 }, // Assume third is price
          total: { index: 3 }, // Assume fourth is total
        },
      };
    }
  }

  // Analyze total amount patterns
  const totalAmounts = samples.map((s) => s.corrected_data.total_amount).filter(Boolean);
  if (totalAmounts.length > 0) {
    // In production, analyze OCR data to find where total appears
    fieldMappings.total_amount = {
      location: 'bottom_right', // Simplified
      pattern: '\\$?([\\d,]+(?:\\.[\\d]{2})?)', // Currency pattern
    };
  }

  // Analyze tax patterns
  const taxAmounts = samples.map((s) => s.corrected_data.tax_amount).filter(Boolean);
  if (taxAmounts.length > 0) {
    fieldMappings.tax = {
      location: 'near_total',
      pattern: '(?:tax|vat|gst)[:\\s]*\\$?([\\d,]+(?:\\.[\\d]{2})?)',
    };
  }

  // Analyze payment method patterns
  const paymentMethods = samples.map((s) => s.corrected_data.payment_method).filter(Boolean);
  if (paymentMethods.length > 0) {
    fieldMappings.payment_method = {
      location: 'footer',
      pattern: '(?:payment|paid|method)[:\\s]*(credit|debit|cash|check|ach|wire|venmo|paypal|zelle)',
    };
  }

  // Calculate confidence based on sample consistency
  const confidence = calculateConfidence(samples);

  const template: ParsingTemplate = {
    company_id: companyId,
    vendor_id: vendorId,
    file_type: fileType as ParsingTemplate['file_type'],
    field_mappings: fieldMappings,
    confidence_score: confidence,
    training_samples: samples.length,
    training_invoice_ids: samples.map((s) => s.invoice_id),
    is_active: true,
  };

  return template;
}

/**
 * Calculate confidence score based on sample consistency
 */
function calculateConfidence(samples: TrainingSample[]): number {
  if (samples.length < 2) return 0;

  // Check consistency of line item counts
  const lineItemCounts = samples.map((s) => s.corrected_data.line_items.length);
  const avgLineItems = lineItemCounts.reduce((a, b) => a + b, 0) / lineItemCounts.length;
  const lineItemVariance =
    lineItemCounts.reduce((sum, count) => sum + Math.abs(count - avgLineItems), 0) /
    lineItemCounts.length;
  const lineItemConsistency = Math.max(0, 1 - lineItemVariance / avgLineItems);

  // Check consistency of total amounts (relative to average)
  const totals = samples.map((s) => s.corrected_data.total_amount).filter(Boolean) as number[];
  if (totals.length > 1) {
    const avgTotal = totals.reduce((a, b) => a + b, 0) / totals.length;
    const totalVariance =
      totals.reduce((sum, total) => sum + Math.abs(total - avgTotal) / avgTotal, 0) / totals.length;
    const totalConsistency = Math.max(0, 1 - totalVariance);

    // Average consistency metrics
    return (lineItemConsistency + totalConsistency) / 2;
  }

  return lineItemConsistency;
}

/**
 * Save parsing template to database
 */
export async function saveParsingTemplate(template: ParsingTemplate): Promise<string> {
  // Deactivate existing templates for this vendor/file_type
  await supabase
    .from('vendor_invoice_parsing_templates')
    .update({ is_active: false })
    .eq('company_id', template.company_id)
    .eq('vendor_id', template.vendor_id)
    .eq('file_type', template.file_type)
    .eq('is_active', true);

  // Insert new template
  const { data, error } = await supabase
    .from('vendor_invoice_parsing_templates')
    .insert(template)
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
}

/**
 * Get active parsing template for a vendor and file type
 */
export async function getParsingTemplate(
  companyId: string,
  vendorId: string,
  fileType: string
): Promise<ParsingTemplate | null> {
  const { data, error } = await supabase
    .from('vendor_invoice_parsing_templates')
    .select('*')
    .eq('company_id', companyId)
    .eq('vendor_id', vendorId)
    .eq('file_type', fileType)
    .eq('is_active', true)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // No rows returned
    throw error;
  }

  return data as ParsingTemplate;
}

/**
 * Test template against an invoice
 * Returns accuracy metrics
 */
export async function testTemplate(
  template: ParsingTemplate,
  invoiceId: string
): Promise<{
  accuracy: number;
  errors: string[];
  extracted_data: Partial<InvoiceData>;
}> {
  // Get invoice data
  const { data: invoice, error } = await supabase
    .from('vendor_invoice_training')
    .select('*')
    .eq('id', invoiceId)
    .single();

  if (error) throw error;

  // Get corrected data if available (for comparison)
  const { data: sample } = await supabase
    .from('vendor_invoice_training_samples')
    .select('*')
    .eq('invoice_id', invoiceId)
    .single();

  const errors: string[] = [];
  let correctFields = 0;
  let totalFields = 0;

  // Test total amount extraction
  if (template.field_mappings.total_amount) {
    totalFields++;
    const extracted = invoice.total_amount;
    const expected = sample?.corrected_total_amount;
    if (expected && Math.abs((extracted || 0) - expected) < 0.01) {
      correctFields++;
    } else if (expected) {
      errors.push(`Total amount mismatch: expected ${expected}, got ${extracted}`);
    }
  }

  // Test line items count
  if (template.field_mappings.line_items) {
    totalFields++;
    const extracted = (invoice.line_items as any[])?.length || 0;
    const expected = (sample?.corrected_line_items as any[])?.length || 0;
    if (extracted === expected) {
      correctFields++;
    } else {
      errors.push(`Line items count mismatch: expected ${expected}, got ${extracted}`);
    }
  }

  const accuracy = totalFields > 0 ? correctFields / totalFields : 0;

  return {
    accuracy,
    errors,
    extracted_data: {
      total_amount: invoice.total_amount,
      line_items: invoice.line_items as any,
    },
  };
}

